import Big from 'big.js'
import { MissionControlSlice } from '../modules/missionControl/slice'
import { LithiumAccountSlice } from '../modules/user/slice'
import { DropSlice } from '../modules/drop/slice'
import { FormQuestionsSlice } from '../modules/formQuestion/slice'
import { SubTask } from '../shared_components/Missions/types'
import { Project } from '../page_components/DropDetails/ChapterView/types'
import { MemecoinsSlice } from '../modules/memecoins/slice'
import { SeasonsSlice } from '../modules/seasons/slice'
import { ethers } from 'ethers'

export interface IWeb3Reducer {
  balance: string
  balanceLPT: string
  balanceEth: string
  balanceTokenSale: string
  activeTokenSaleAddress: string
  account: string
  newClaimChain: number
  desiredNetworkId: number
  isEthRaise: boolean
  ethUsdPrice: number
}

export interface StakeInformation {
  baseApy: string
  vaultsApy: string
  daysRemainingOfVesting: string
  currentTotalDailyTokensVested: string
  tokensVestedAtNextHalvingPeriod: string
  daysUntilNextHalvingEvent: string
  estimatedTokensOverStake: string
}

export type TierIndex = number
export interface IStakingReducer {
  stakedAmount: Big
  tier: number
  timeStaked: string
  timeUnlock: number
  tierReference: TierReference
  lockLevel: string
  stakeInformation:
  | {
    [key: TierIndex]: StakeInformation
  }
  | undefined
}

export interface IAccessReducer {
  isAdmin: boolean
  admins: Array<string>
}

export enum Tiers {
  NONE,
  STARTER,
  INVESTOR,
  STRATEGIST,
  EVANGELIST,
}

export type TierReference = {
  [key: string]: {
    [key: string]: {
      allocation: string
      required: string
    }
  }
}

export enum PoolStatusType {
  INCOMING = 'INCOMING',
  REVIEWING = 'REVIEWING',
  DENY = 'DENY',
  SCHEDULED = 'SCHEDULED',
  COMPLETED = 'COMPLETED',
}

export type EscrowMilestoneType = {
  milestone: number
  return: number
}

export type TeamMemberType = {
  name: string
  position: string
  links: Array<TitledLinkType>
  avatar: null | string
}

export type TitledLinkType = {
  title: string
  link: string
}
export type Investor = {
  name: string
  imageUrl: string
  href: string
}

export type ProjectAboutType = {
  logo: {
    url: string
  }
  highlightsText?: string
  links: Array<TitledLinkType>
  productText?: string
  problemText?: string
  solutionText?: string
  businessModalText?: string
  marketText?: string
  investorsText?: string
  tokenUtility?: string
  socialLinks?: string
  description?: string
  investors?: Investor[]
  vestingSchedule?: SortableVestingScheduleItem[]
}

export type SaleType = {
  price: string
  start: string
  end: string
  cap: number | Array<number>
}

export type Sold = {
  total: Big
  private: Big
  public: Big
}

export type FeaturedProjectImage = {
  data: { id: number; attributes: { url: string } }[]
}

export type Media = {
  featuredProjectImage: { url: string } | FeaturedProjectImage
  highlightsVideo: { url: string }
  projectThumbnail: { url: string }
  topBanner: { url: string }
}
export type TopLinks = {
  [key: string]: string
}

export interface VestingSettings {
  vestingPeriodStart: Date
  vestingInvervalInSeconds: string
  numberOfVestingPoints: number
  firstVestPercentage: number
  differentInitialVestingValue: boolean
}

export interface PoolSettings {
  tokenAddress: string
  totalSupplyOfToken: number
  totalSupplyForSale: number
  baselineAllocation: number
  totalRaiseLimit: number
  requiresKYC: boolean
  liquidityPercentage: number
  burnUnsold: boolean
  totalTokensForLiquidity: number
  totalPercentageOfFundsRaisedForLiquidity: number
  liquidityLockPeriod: number
  tokenVestingPeriod: number
  marketCapAtLaunch: number
  circulatingMarketCapAtLaunch: number
  exchange: 'thruster'
  usingOwnToken: boolean
  verifiedTokenAddress: string
  publishPoolInstantly: boolean
  tgePrice: number
  tgePriceRange?: TGEPriceRange
  createdBy: string
  taxRate: number
  isEthRaise: boolean
  lpLockInPeriodUnit: 'minutes' | 'months' | 'years'
  lpLockInPeriodAmount: number
  isInstantLiquidityGeneration: boolean
  liquidityPoolGenerationTime: string
  raiseCurrency: 'eth' | 'usdb'
  vestingSettings?: VestingSettings
  softCap?: number
}

export interface Audit {
  url?: string
  status?: string
  automatedAudit: {
    id: string
    // Very Low, Low, Neutral, High, Very High
    rank: string
    totalScore: number
    communityScore: number
    securityScore: number
    marketScore: number
    decentralisationScore: number
    fundamentalsScore: number
    auditScore: number
    address: string
    slug: string
    createdAt: string
    expiresAt: string
    fetchedAt: string
  }
}

export type SortableVestingScheduleItem = {
  Allocation: string
  ['Allocation %']: number
  ['Amount']: number
  Price?: number
  Raise?: number
  ['TGE %']?: number
  Vesting: string
}

export interface IPool {
  id: number
  publicBuyLimit?: Big
  airDrop: Big
  escrowBlock: any
  totalSaleSupply: Big
  thresholdPublicAmount: Big
  token: string
  createToken: boolean
  name: string
  initialAddress: string
  poolAddress: string
  ticker: string
  totalSupply?: Big
  sold: Sold
  decimals: string
  escrow: number
  publicThreshold: Big
  escrowMilestones: Array<EscrowMilestoneType>
  status: PoolStatusType
  privateSale: SaleType
  publicSale: SaleType
  blacklist?: Array<string>
  about: ProjectAboutType
  team: Array<TeamMemberType>
  isPoolSoldOut?: boolean
  vestingPoints: VestingPoints[]
  poolContract?: any
  isDeployed?: boolean
  mainNet?: boolean
  isOldPool?: boolean
  chainID?: string
  topBannerDark?: { url: string }
  topBannerLight?: { url: string }
  crossChainLink?: string
  media?: Media
  listingType?: string
  vestingTableRow?: {
    percent: number
    label: string
  }[]
  isFeaturedProject?: boolean
  claimLocation?: string
  topLinks?: TopLinks
  athSinceIDO?: number
  isBusd?: boolean
  isLegacyPool?: boolean
  isTBC?: boolean
  isTbc?: boolean
  progress?: number
  tgePrice?: number
  poolSettings?: PoolSettings
  whitelist?: any
  slug?: string
  hasCollectedUsdb?: boolean
  isVetted?: boolean
  isEthRaise?: boolean
  clientVerifications?: {
    kyc?: {
      status?: string
      url?: string
    }
    doxx?: {
      status?: string
      url?: string
    }
  }
  audit?: Audit
}

export type TGEPriceRange = [TGEPriceRangeLow, TGEPriceRangeHigh]
type TGEPriceRangeLow = number
type TGEPriceRangeHigh = number

export interface DropSettings {
  createdBy: string
  end?: Date
  start?: Date
  distribution?: DropDistribution
  vestingSettings?: VestingSettings
  rewardsSettings?: Array<RewardSettings>
}

export interface RewardSettings {
  tokenAddress?: string
  verifiedTokenAddress?: string
  exchange?: 'thruster'
  totalSupplyForSale?: number
  totalSupplyOfToken?: number
  ticker?: string
  name?: string
  decimals?: number
  image?: {
    url: string
  }
  description?: string
  distributionDisclaimer?: string
  vestingDisclaimer?: string
}

export interface DropDistribution {
  n: number
  m: number
  minLevel: number
}

export interface IDrop {
  id: number
  name: string
  chapterName?: string
  // not a model field, its just used in the drops projects list, it comes computed from the api return
  chapterIndex?: number
  isFeaturedProject?: boolean
  slug?: string
  ticker?: string
  decimals: number
  isTbc: boolean
  createdAt?: Date
  updatedAt?: Date
  publishedAt?: Date
  about?: ProjectAboutType
  media?: Media
  team?: Array<TeamMemberType>
  socials?: TopLinks
  dropSettings?: DropSettings
  projectId: number
  project?: Project
  parentDropId: number
  parentDrop?: ParentDrop
  isDeployed: boolean
  isPublic: boolean
  chainId?: string
}

export interface ParentDrop {
  id: number
  slug: string
  order?: number
  totalRewardText?: string
  isHighlighted: boolean
  createdAt?: Date
  updatedAt?: Date
}
export interface Memecoin {
  _id: string
  createdAt: string
  tokenName: string
  tokenSymbol: string
  description: string
  tokenAddress?: string
  poolAddress?: string
  liquidityPoolAddress?: string
  creatorAddress?: string
  links?: { label: string; url: string }[]
  comments?: MemecoinComment[]
  meta?: {
    marketCap?: number
    ethRaised?: MemecoinMetaValue
    price24HrsAgo?: MemecoinMetaValue
    ethOutForOneToken?: MemecoinMetaValue
    totalTxns?: number
    latestTxnTimestamp?: Date
  }
  media: {
    logo: {
      url: string
    }
    banner: {
      url?: string
    }
  }
  socials?: MemecoinSocial[]
  chainId: string
  decimals: number
  isFourEthPool?: boolean
  latestTransaction: TransactionEvent
}

interface MemecoinMetaValue {
  value: string
  updatedAt: string
}

export type Comment = {
  _id: string
  createdAt: string
  text: string
  user: {
    id: string
    name: string
    avatar: string
    username: string
  }
}

export type MemecoinSocial = {
  label: string
  url: string
}

export type MemecoinCommentReaction = {
  _id: string
  emoji: string
  author: {
    walletAddress: string
  }
}

export type MemecoinCommentReply = {
  _id: string
  content: string
  reactions: MemecoinCommentReaction[]
  author: {
    walletAddress: string
    metadata: TwitterUserMetadata
  }
  createdAt: Date
}

export type MemecoinComment = {
  _id: string
  content: string
  poolAddress: string
  reactions: MemecoinCommentReaction[]
  author: {
    walletAddress: string
    metadata: TwitterUserMetadata
  }
  replies: MemecoinCommentReply[]
  createdAt: Date
}

export type TransactionEvent = {
  _id: string
  event: string
  transactionHash: string
  walletAddress: string
  contractAddress: string
  timestamp: string
  nansenScore: number
  meta: {
    amountIn: string
    amountOut: string
  }
  user?: {
    metadata?: TwitterUserMetadata
  }
}

export type TwitterUserMetadata = {
  id: string
  username: string
  name: string
  verified: boolean
  profile_image_url: string
  public_metrics: {
    like_count: number
    tweet_count: number
    listed_count: number
    followers_count: number
    following_count: number
  }
}

export interface IContract {
  abi: ethers.InterfaceAbi
  address: string
  contractInterfaceFields?: { id: string; poolName: string }[]
}
export interface IPoolProps {
  id: number
  media?: Media
  params: IBUSDParams
  address: string
  sold?: Sold
  time?: IMasterTimeParams
  period: string
  urlLogo?: string
  contacts?: {
    master: IContract
    stakes: IContract
  }
  cmsData?: IPool
}

export interface IOldPoolProps {
  params: IMasterParams
  address: string
  sold?: Sold
  time?: IMasterTimeParams
  period: string
  contracts: {}
}

export interface IPoolListProps {
  active: IPoolProps[]
  upcoming: IPoolProps[]
  completed: IPoolProps[]
  mostTraction: IPoolProps[]
  recentlyLaunched: IPoolProps[]
  closingSoon: IPoolProps[]
  mostFunded: IPoolProps[]
  loading: boolean
  oldPoolsAddress: IPoolProps[]
  serialisedPools: { [key: string]: any }
}

export interface IPoolListGroupedProps {
  all: {
    pools: IPoolProps[]
    loading: boolean
    total: number
  }
  labs: IPoolListProps
  launch: IPoolListProps
}

export interface IDropListProps {
  all: IDrop[]
  active: IDrop[]
  upcoming: IDrop[]
  completed: IDrop[]
  mostTraction: IDrop[]
  recentlyLaunched: IDrop[]
  closingSoon: IDrop[]
  mostFunded: IDrop[]
  loading: boolean
  total: number
}

export interface IMasterTimeParams {
  privatePoolSaleStartTime: string
  privatePoolSaleEndTime: string
  publicPoolSaleStartTime: string
  publicPoolSaleEndTime: string
  democracyHourEndTimestamp: string
}

export interface VestingPoints {
  timestamp: string
  pct: string
}

export type Plan = {
  name: string
  required: string
  allocation: string
  lockInPeriod: number
}

export type ReferralProject = {
  referral?: {
    id: number
    code: string
    taskId: number
    projectId: number
    referrerId: number
  }
  task?: SubTask
  pool?: IPool
  drop?: IDrop
}

export interface IMasterParams {
  decimals: number
  initialAddress: string
  token: string
  totalSaleSupply: Big
  poolSupplyCounted: Big
  privateTokenPrice: Big
  publicTokenPrice: Big
  escrowPercentage: string
  thresholdPublicAmount: Big
  tierPrices: Array<Big>
  escrowReturnMilestones: Array<Array<String>>
  escrowPercentageCounted: Big
  airDrop: Big
  tokenName: string
  tokenSymbol: string
  claimTime?: any
  isDeployed?: boolean
  isOldPool?: boolean
  mainNet?: boolean
  vestingPoints: VestingPoints[]
  chainID?: number
  isBusd?: boolean
}

export interface IBUSDParams {
  initial: string
  privateStart: number
  privateEnd: number
  privateTokenPrice: Big
  token: string
  totalSaleSupply: Big
  vestingPoints: string
  tokenName: string
  tokenSymbol: string
  isBusd: boolean
  cmsDetails: IPool
}

export interface MiscUI {
  headerBgUrl: string
  pageBgUrl: string
  isDarkTheme: boolean
  hideBurger: boolean
  shouldScrollToProjects: boolean
  shouldResetRewardsNotification: boolean
  topBarContent: string | null
  topBarUrlLink: string | null
  allPoolsLastPage: number
  multiStepTxCurrentStep: number
  multiStepTxTotalSteps: number
  allocationOpenTabs: number[]
  resetAllClaims: boolean
  allocationIndexClaiming: number
  topBarRightContent: boolean
  topBarHideMobile: boolean
  investmentAmountForCheckout: number
  isCheckoutScreen: boolean
  planForCheckout: Plan
  hasCompletedPlusCheckout: boolean
  showRadialGlow: boolean
  rewardsAmount: number
  headerHeight: number
  isLaunch: boolean
  isMemecoins: boolean
  isDrops: boolean
  showHeaderGradient: boolean
  showDashboardHeaderGradient: boolean
  referralProject?: {
    data?: ReferralProject
    isLoading?: boolean
    isError?: boolean
  }
  twitterWarningNotice: string | null
  discordWarningNotice: string | null
  bottomNavButton: BottomNavButton | null
  memecoinsSearchQuery: string
  dismissableModal: DismissableModal | null
}

export type DismissableModal = 'quest_complete'

export type BottomNavButton =
  | 'acknowledge_season_end'
  | 'acknowledge_season_start'
  | 'back_to_season'
  | 'claim_season_prizes'

export interface IRootReducer {
  api: any
  web3: IWeb3Reducer
  staking: IStakingReducer
  listPool: IPoolListGroupedProps
  listDrop: IDropListProps
  pool: IPool
  drop: DropSlice
  access: IAccessReducer
  miscUI: MiscUI
  missionControl: MissionControlSlice
  user: LithiumAccountSlice
  formQuestions: FormQuestionsSlice
  memecoins: MemecoinsSlice
  seasons: SeasonsSlice
}

export const initTiers: TierReference = {
  '0': {
    Backer: {
      allocation: '15',
      required: '2000000000000000000000',
    },
    Starter: {
      allocation: '75',
      required: '6000000000000000000000',
    },
    Investor: {
      allocation: '150',
      required: '10000000000000000000000',
    },
    Strategist: {
      allocation: '350',
      required: '25000000000000000000000',
    },
    Venturist: {
      allocation: '750',
      required: '50000000000000000000000',
    },
    Evangelist: {
      allocation: '1125',
      required: '70000000000000000000000',
    },
  },
  '30': {
    Backer: {
      allocation: '30',
      required: '2000000000000000000000',
    },
    Starter: {
      allocation: '115',
      required: '6000000000000000000000',
    },
    Investor: {
      allocation: '225',
      required: '10000000000000000000000',
    },
    Strategist: {
      allocation: '550',
      required: '25000000000000000000000',
    },
    Venturist: {
      allocation: '1125',
      required: '50000000000000000000000',
    },
    Evangelist: {
      allocation: '1650',
      required: '70000000000000000000000',
    },
  },
  '60': {
    Backer: {
      allocation: '60',
      required: '2000000000000000000000',
    },
    Starter: {
      allocation: '225',
      required: '6000000000000000000000',
    },
    Investor: {
      allocation: '350',
      required: '10000000000000000000000',
    },
    Strategist: {
      allocation: '825',
      required: '25000000000000000000000',
    },
    Venturist: {
      allocation: '1650',
      required: '50000000000000000000000',
    },
    Evangelist: {
      allocation: '2350',
      required: '70000000000000000000000',
    },
  },
  '90': {
    Backer: {
      allocation: '200',
      required: '2000000000000000000000',
    },
    Starter: {
      allocation: '600',
      required: '6000000000000000000000',
    },
    Investor: {
      allocation: '850',
      required: '10000000000000000000000',
    },
    Strategist: {
      allocation: '1650',
      required: '25000000000000000000000',
    },
    Venturist: {
      allocation: '2350',
      required: '50000000000000000000000',
    },
    Evangelist: {
      allocation: '3000',
      required: '70000000000000000000000',
    },
    'Evangelist Pro': {
      allocation: '7500',
      required: '300000000000000000000000',
    },
  },
}
